import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs';
import { AuthService, UserInfo } from '../../services/auth.service';

/**
 * Redirects authenticated user to the correct default screen by role (permission matrix).
 *
 * IMPORTANT: do NOT call router.navigate(['/unauthorized']) here for the null-user case.
 * User info loads asynchronously after the MSAL redirect — we must wait for user$ to emit
 * before deciding where to send the user, otherwise IT/L&D users see a flash of Access Denied.
 */
@Component({
  selector: 'app-default-redirect',
  standalone: true,
  template: '<p>Redirecting...</p>'
})
export class DefaultRedirectComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  constructor(private authService: AuthService, private router: Router) {}

  ngOnInit(): void {
    const user = this.authService.getUser();
    if (user) {
      this.redirect(user);
      return;
    }
    // User info not yet loaded (race condition after MSAL redirect) — wait for first emission
    this.authService.user$
      .pipe(
        filter((u): u is UserInfo => u !== null),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(u => this.redirect(u));
  }

  private redirect(user: UserInfo): void {
    if (user.isAdmin || user.isPM) {
      this.router.navigate(['/dashboard/portfolio-overview']);
    } else if (user.isFinance) {
      this.router.navigate(['/allocations']);
    } else if (user.isPc) {
      this.router.navigate(['/employees']);
    } else if (user.isSales) {
      this.router.navigate(['/presales/summary']);
    } else if (user.isSoftware || user.isAccounting || user.isMarketing || user.isOperations || user.isSupport) {
      this.router.navigate(['/timesheet']);
    } else if (user.isIt || user.isLnD) {
      this.router.navigate(['/onboarding/employees']);
    } else {
      this.router.navigate(['/unauthorized']);
    }
  }
}
